import {invalidateOnSuccess, metadaApi} from '@dms/api/base';

import {
  BulkPatchDirectSaleLabourBasketItemRequest,
  BulkPatchDirectSaleLabourBasketItemRequestSchema,
  BulkPatchDirectSaleLabourBasketItemResponse,
  BulkPatchDirectSaleLabourBasketItemResponseSchema,
  DeleteDirectSaleLabourBasketItemRequest,
  DeleteDirectSaleLabourBasketItemRequestSchema,
  DeleteDirectSaleLabourBasketItemResponse,
  DeleteDirectSaleLabourBasketItemResponseSchema,
  DeleteDirectSaleLabourBasketItemsRequest,
  DeleteDirectSaleLabourBasketItemsRequestSchema,
  DeleteDirectSaleLabourBasketItemsResponse,
  DeleteDirectSaleLabourBasketItemsResponseSchema,
  GetDirectSaleLabourBasketItemsRequest,
  GetDirectSaleLabourBasketItemsRequestSchema,
  GetDirectSaleLabourBasketItemsResponse,
  GetDirectSaleLabourBasketItemsResponseSchema,
  PatchDirectSaleLabourBasketItemQuantityRequest,
  PatchDirectSaleLabourBasketItemQuantityRequestSchema,
  PatchDirectSaleLabourBasketItemQuantityResponse,
  PatchDirectSaleLabourBasketItemQuantityResponseSchema,
  PostDirectSaleLabourItemsMechanicRequest,
  PostDirectSaleLabourItemsMechanicRequestSchema,
  PostDirectSaleLabourItemsMechanicResponse,
  PostDirectSaleLabourItemsMechanicResponseSchema,
  PostDirectSaleLabourItemToBasketRequest,
  PostDirectSaleLabourItemToBasketRequestSchema,
  PostDirectSaleLabourItemToBasketResponse,
  PostDirectSaleLabourItemToBasketResponseSchema,
} from './types';

export const metadaWarehouseDirectSaleLabourBasketApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getDirectSaleLabourBasketItems: build.query<
      GetDirectSaleLabourBasketItemsResponse,
      GetDirectSaleLabourBasketItemsRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/${queryArg.directSaleId}/labour-cart`,
        method: 'GET',
      }),
      providesTags: ['directSaleLabourBasket'],
      extraOptions: {
        requestSchema: GetDirectSaleLabourBasketItemsRequestSchema,
        responseSchema: GetDirectSaleLabourBasketItemsResponseSchema,
      },
    }),
    deleteDirectSaleLabourBasketItems: build.mutation<
      DeleteDirectSaleLabourBasketItemsResponse,
      DeleteDirectSaleLabourBasketItemsRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/${queryArg.directSaleId}/labour-cart`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess(
        (result, queryArg: DeleteDirectSaleLabourBasketItemsRequest) => [
          'directSaleLabourBasket',
          {type: 'directSales', id: queryArg.directSaleId},
        ]
      ),
      extraOptions: {
        requestSchema: DeleteDirectSaleLabourBasketItemsRequestSchema,
        responseSchema: DeleteDirectSaleLabourBasketItemsResponseSchema,
      },
    }),
    postDirectSaleLabourBasketCheckout: build.mutation<
      PostDirectSaleLabourItemsMechanicResponse,
      PostDirectSaleLabourItemsMechanicRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/${queryArg.directSaleId}/labour-cart/checkout`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess(
        (result, queryArg: PostDirectSaleLabourItemsMechanicRequest) => [
          'directSaleLabourBasket',
          {type: 'directSales', id: queryArg.directSaleId},
          'afsCheckout',
          'afsPaymentsList',
          'afsPaymentById',
          'headerDynamicActions',
        ]
      ),
      extraOptions: {
        requestSchema: PostDirectSaleLabourItemsMechanicRequestSchema,
        responseSchema: PostDirectSaleLabourItemsMechanicResponseSchema,
      },
    }),
    postDirectSaleLabourItemToBasket: build.mutation<
      PostDirectSaleLabourItemToBasketResponse,
      PostDirectSaleLabourItemToBasketRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/${queryArg.directSaleId}/labour-cart/item/labour-catalog`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: ['directSaleLabourBasket'],
      extraOptions: {
        requestSchema: PostDirectSaleLabourItemToBasketRequestSchema,
        responseSchema: PostDirectSaleLabourItemToBasketResponseSchema,
      },
    }),
    patchDirectSaleLabourBasketItemQuantity: build.mutation<
      PatchDirectSaleLabourBasketItemQuantityResponse,
      PatchDirectSaleLabourBasketItemQuantityRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/${queryArg.directSaleId}/labour-cart/item/${queryArg.itemId}/quantity`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: ['directSaleLabourBasket', 'directSaleLabourItems'],
      extraOptions: {
        requestSchema: PatchDirectSaleLabourBasketItemQuantityRequestSchema,
        responseSchema: PatchDirectSaleLabourBasketItemQuantityResponseSchema,
      },
    }),
    deleteDirectSaleLabourBasketItem: build.mutation<
      DeleteDirectSaleLabourBasketItemResponse,
      DeleteDirectSaleLabourBasketItemRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/${queryArg.directSaleId}/labour-cart/item/${queryArg.itemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['directSaleLabourBasket'],
      extraOptions: {
        requestSchema: DeleteDirectSaleLabourBasketItemRequestSchema,
        responseSchema: DeleteDirectSaleLabourBasketItemResponseSchema,
      },
    }),
    bulkPatchDirectSaleLabourBasketItem: build.mutation<
      BulkPatchDirectSaleLabourBasketItemResponse,
      BulkPatchDirectSaleLabourBasketItemRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale/${queryArg.directSaleId}/item-labour/edit`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: [
        'directSaleLabourBasket',
        'directSaleLabourItems',
        'afsCheckout',
        'afsPaymentsList',
        'afsPaymentById',
      ],
      extraOptions: {
        requestSchema: BulkPatchDirectSaleLabourBasketItemRequestSchema,
        responseSchema: BulkPatchDirectSaleLabourBasketItemResponseSchema,
      },
    }),
  }),
});

export const {
  useGetDirectSaleLabourBasketItemsQuery,
  useDeleteDirectSaleLabourBasketItemsMutation,
  usePostDirectSaleLabourBasketCheckoutMutation,
  usePostDirectSaleLabourItemToBasketMutation,
  usePatchDirectSaleLabourBasketItemQuantityMutation,
  useDeleteDirectSaleLabourBasketItemMutation,
  useBulkPatchDirectSaleLabourBasketItemMutation,
} = metadaWarehouseDirectSaleLabourBasketApi;
